const CAPABILITIES = Object.freeze({
    ANALYSIS_BASIC: 'analysis.basic',
    ANALYSIS_AI: 'analysis.ai',
    HISTORY_ACCESS: 'history.access',
    EXPORT_DOWNLOAD: 'export.download'
});

const QUOTAS = Object.freeze({
    ANALYSIS_AI: 'analysis.ai'
});

const FALLBACK_TIER_ID = 'free';
const DEFAULT_TIER_ORDER = ['free', 'standard', 'pro'];

// 年度订阅促销文案 - 已迁移至i18n
const YEARLY_DISCOUNT_BADGE_TEXT = 'landing.pricing.badge.yearlyDiscount';

function createDefaultTiers() {
    return {
        free: {
            id: 'free',
            label: 'landing.pricing.tiers.free',
            priority: 0,
            capabilities: [],
            quotas: {
                [QUOTAS.ANALYSIS_AI]: 0
            },
            pricing: {
                'en-US': {
                    monthly: {
                        price: 0,
                        currency: 'USD'
                    }
                }
            },
            displayInfo: {
                titleKey: 'dashboard.plans.free.title',
                periodKey: 'dashboard.plans.free.period',
                featuresKeys: [
                    'dashboard.plans.free.features.0',
                    'dashboard.plans.free.features.1',
                    'dashboard.plans.free.features.2',
                    'dashboard.plans.free.features.3',
                    'dashboard.plans.free.features.4',
                    'dashboard.plans.free.features.5'
                ]
            }
        },
        standard: {
            id: 'standard',
            label: 'landing.pricing.tiers.standard',
            priority: 1,
            capabilities: [
                CAPABILITIES.ANALYSIS_AI
            ],
            quotas: {
                [QUOTAS.ANALYSIS_AI]: 1
            },
            pricing: {
                'en-US': {
                    monthly: {
                        price: 9.99,
                        originalPrice: 14.99,
                        currency: 'USD'
                    },
                    yearly: {
                        price: 99,
                        originalPrice: 179.88,
                        currency: 'USD'
                    }
                }
            },
            displayInfo: {
                titleKey: 'dashboard.plans.standard.title',
                periodKey: 'dashboard.plans.standard.period',
                subtitleKey: 'dashboard.plans.standard.subtitle',
                featuresKeys: [
                    'dashboard.plans.standard.features.0',
                    'dashboard.plans.standard.features.1',
                    'dashboard.plans.standard.features.2',
                    'dashboard.plans.standard.features.3',
                    'dashboard.plans.standard.features.4',
                    'dashboard.plans.standard.features.5'
                ]
            }
        },
        pro: {
            id: 'pro',
            label: 'landing.pricing.tiers.pro',
            priority: 2,
            capabilities: [
                CAPABILITIES.ANALYSIS_AI
            ],
            quotas: {
                [QUOTAS.ANALYSIS_AI]: null
            },
            pricing: {
                'en-US': {
                    monthly: {
                        price: 18.99,
                        originalPrice: 29.99,
                        currency: 'USD'
                    },
                    yearly: {
                        price: 190,
                        originalPrice: 359.88,
                        currency: 'USD'
                    }
                }
            },
            displayInfo: {
                titleKey: 'dashboard.plans.pro.title',
                periodKey: 'dashboard.plans.pro.period',
                subtitleKey: 'dashboard.plans.pro.subtitle',
                featuresKeys: [
                    'dashboard.plans.pro.features.0',
                    'dashboard.plans.pro.features.6',
                    'dashboard.plans.pro.features.1',
                    'dashboard.plans.pro.features.2',
                    'dashboard.plans.pro.features.3',
                    'dashboard.plans.pro.features.4',
                    'dashboard.plans.pro.features.5'
                ],
                isRecommended: true,
                recommendedBadgeKey: 'dashboard.plans.pro.badge'
            }
        }
    };
}

function clone(value) {
    return JSON.parse(JSON.stringify(value));
}

function sanitizeTierConfig(tierId, tier) {
    const safeTier = tier && typeof tier === 'object' ? { ...tier } : {};
    safeTier.id = tierId;
    safeTier.label = typeof safeTier.label === 'string' && safeTier.label.trim()
        ? safeTier.label.trim()
        : tierId.charAt(0).toUpperCase() + tierId.slice(1);
    safeTier.capabilities = Array.isArray(safeTier.capabilities)
        ? Array.from(new Set(safeTier.capabilities.filter(Boolean)))
        : [];
    safeTier.quotas = safeTier.quotas && typeof safeTier.quotas === 'object'
        ? { ...safeTier.quotas }
        : {};
    return safeTier;
}

function buildConfig(overrides = {}) {
    const base = {
        tiers: createDefaultTiers(),
        order: [...DEFAULT_TIER_ORDER],
        defaultTier: FALLBACK_TIER_ID
    };

    if (overrides && typeof overrides === 'object') {
        if (overrides.tiers && typeof overrides.tiers === 'object') {
            Object.entries(overrides.tiers).forEach(([tierId, tierValue]) => {
                base.tiers[tierId] = sanitizeTierConfig(tierId, {
                    ...base.tiers[tierId],
                    ...clone(tierValue)
                });
            });
        }

        if (Array.isArray(overrides.order) && overrides.order.length > 0) {
            base.order = overrides.order.filter(tierId => typeof tierId === 'string');
        }

        if (typeof overrides.defaultTier === 'string') {
            base.defaultTier = overrides.defaultTier;
        }
    }

    const tierIds = Object.keys(base.tiers);
    base.order = base.order.filter(tierId => tierIds.includes(tierId));
    if (base.order.length === 0) {
        base.order = [...tierIds];
    }

    Object.entries(base.tiers).forEach(([tierId, tierValue]) => {
        base.tiers[tierId] = sanitizeTierConfig(tierId, tierValue);
    });

    if (!tierIds.includes(base.defaultTier)) {
        base.defaultTier = FALLBACK_TIER_ID;
    }

    return {
        tiers: base.tiers,
        order: base.order,
        defaultTier: base.defaultTier
    };
}

function readRuntimeOverrides() {
    if (typeof window === 'undefined') {
        return null;
    }
    const { __SUBSCRIPTION_CONFIG__ } = window;
    if (!__SUBSCRIPTION_CONFIG__ || typeof __SUBSCRIPTION_CONFIG__ !== 'object') {
        return null;
    }
    return __SUBSCRIPTION_CONFIG__;
}

export function getDefaultSubscriptionConfig() {
    const overrides = readRuntimeOverrides();
    const config = buildConfig(overrides || undefined);
    return {
        tiers: clone(config.tiers),
        order: [...config.order],
        defaultTier: config.defaultTier
    };
}

export function createTierPriorityMap(order = DEFAULT_TIER_ORDER) {
    return order.reduce((acc, tierId, index) => {
        acc[tierId] = index;
        return acc;
    }, {});
}

export {
    CAPABILITIES as SUBSCRIPTION_CAPABILITIES,
    QUOTAS as SUBSCRIPTION_QUOTAS,
    FALLBACK_TIER_ID,
    YEARLY_DISCOUNT_BADGE_TEXT
};
